import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Spinner,
  Alert,
  Form,
  Badge,
  Row,
  Col,
} from "react-bootstrap";
import {
  FaFolder,
  FaFolderOpen,
  FaFilePdf,
  FaVideo,
  FaFileAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaCheckDouble,
  FaTimes,
  FaSearch,
} from "react-icons/fa";
import { toast } from "react-toastify";
import BatchService from "../../../../services/batchService";

const BatchCourseMaterialsTab = ({ batchId, courseName }) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [updating, setUpdating] = useState(false);
  const [updatingMaterialId, setUpdatingMaterialId] = useState(null);

  useEffect(() => {
    loadMaterials();
  }, [batchId]);

  const loadMaterials = async () => {
    try {
      setLoading(true);
      const response = await BatchService.getBatchCourseMaterials(batchId);
      const processedMaterials = processMaterials(response.data.materials || []);
      setMaterials(processedMaterials);
    } catch (error) {
      toast.error("Failed to load course materials");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const processMaterials = (items) => {
    return items.map((item) => {
      let isEnabled = item.is_enabled === 1 || item.is_enabled === true || item.is_enabled === "1";
      
      const processedItem = {
        ...item,
        is_enabled: isEnabled
      };
      
      if (item.children && item.children.length > 0) {
        processedItem.children = processMaterials(item.children);
      }
      return processedItem;
    });
  };

  // Get all child material IDs recursively (including all descendants)
  const getAllChildMaterialIds = (material) => {
    let ids = [];
    
    const collectIds = (item) => {
      if (item.children && item.children.length > 0) {
        item.children.forEach(child => {
          ids.push(child.id);
          if (child.children && child.children.length > 0) {
            collectIds(child);
          }
        });
      }
    };
    
    collectIds(material);
    return ids;
  };

  // Get all child materials WITH their IDs for updating state
  const getAllChildMaterialIdsWithPath = (material, targetIds = []) => {
    if (material.children && material.children.length > 0) {
      material.children.forEach(child => {
        targetIds.push(child.id);
        if (child.children && child.children.length > 0) {
          getAllChildMaterialIdsWithPath(child, targetIds);
        }
      });
    }
    return targetIds;
  };

  // Update a single material in the tree
  const updateSingleMaterial = (materialId, newEnabledStatus) => {
    setMaterials(prevMaterials => {
      const updateRecursive = (items) => {
        return items.map((item) => {
          if (item.id === materialId) {
            return { ...item, is_enabled: newEnabledStatus };
          }
          if (item.children && item.children.length > 0) {
            return { ...item, children: updateRecursive(item.children) };
          }
          return item;
        });
      };
      return updateRecursive(prevMaterials);
    });
  };

  // Update multiple materials in the tree (for folder children) - FIXED VERSION
  const updateMultipleMaterials = (materialIds, newEnabledStatus) => {
    setMaterials(prevMaterials => {
      const updateRecursive = (items) => {
        return items.map((item) => {
          // Update current item if ID matches
          let updatedItem = { ...item };
          
          if (materialIds.includes(item.id)) {
            updatedItem.is_enabled = newEnabledStatus;
          }
          
          // Recursively update children
          if (updatedItem.children && updatedItem.children.length > 0) {
            updatedItem.children = updateRecursive(updatedItem.children);
          }
          
          return updatedItem;
        });
      };
      return updateRecursive(prevMaterials);
    });
  };

  // Toggle a single material (for non-folder items)
  const toggleMaterial = async (materialId, currentStatus) => {
    setUpdating(true);
    setUpdatingMaterialId(materialId);
    
    // Optimistic update - change UI immediately
    updateSingleMaterial(materialId, !currentStatus);
    
    try {
      const response = await BatchService.toggleCourseMaterial(batchId, materialId);
      
      if (response.success) {
        const newStatus = response.data?.is_enabled === 1 || response.data?.is_enabled === true;
        updateSingleMaterial(materialId, newStatus);
        toast.success(newStatus ? "Material enabled for students" : "Material hidden from students");
      } else {
        // Revert on error
        updateSingleMaterial(materialId, currentStatus);
        toast.error("Failed to update material visibility");
      }
    } catch (error) {
      console.error("Toggle error:", error);
      updateSingleMaterial(materialId, currentStatus);
      toast.error("Failed to update material visibility");
    } finally {
      setUpdating(false);
      setUpdatingMaterialId(null);
    }
  };

  // Toggle a folder and all its children - FIXED VERSION with proper child updates
  const toggleFolderWithChildren = async (folder, currentStatus) => {
    // Get ALL child IDs (including nested children)
    const childIds = getAllChildMaterialIdsWithPath(folder, []);
    const newStatus = !currentStatus;
    
    console.log("Toggling folder:", folder.name);
    console.log("Child IDs to update:", childIds);
    console.log("Current status:", currentStatus, "New status:", newStatus);
    
    setUpdating(true);
    setUpdatingMaterialId(folder.id);
    
    // Step 1: Update UI immediately for folder AND all children
    updateSingleMaterial(folder.id, newStatus);
    if (childIds.length > 0) {
      updateMultipleMaterials(childIds, newStatus);
    }
    
    try {
      // Step 2: Toggle the folder itself via API
      await BatchService.toggleCourseMaterial(batchId, folder.id);
      
      // Step 3: Toggle all children via API
      for (const childId of childIds) {
        await BatchService.toggleCourseMaterial(batchId, childId);
      }
      
      toast.success(
        newStatus 
          ? `✅ "${folder.name}" and ${childIds.length} item(s) enabled for students`
          : `❌ "${folder.name}" and ${childIds.length} item(s) hidden from students`
      );
    } catch (error) {
      console.error("Toggle folder error:", error);
      // Revert on error - update UI back to original state
      updateSingleMaterial(folder.id, currentStatus);
      if (childIds.length > 0) {
        updateMultipleMaterials(childIds, currentStatus);
      }
      toast.error("Failed to update folder visibility");
    } finally {
      setUpdating(false);
      setUpdatingMaterialId(null);
    }
  };

  // Handle checkbox click
  const handleCheckboxChange = (item, currentStatus) => {
    console.log("Checkbox clicked for:", item.name, "Type:", item.type, "Current:", currentStatus);
    
    if (item.type === "folder") {
      // For folders, toggle folder + all children
      toggleFolderWithChildren(item, currentStatus);
    } else {
      // For non-folder items, toggle only itself
      toggleMaterial(item.id, currentStatus);
    }
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  const enableAll = async () => {
    const allMaterialIds = getAllMaterialIds(materials);
    if (allMaterialIds.length === 0) {
      toast.info("No materials to enable");
      return;
    }
    
    setUpdating(true);
    // Optimistic update - enable all immediately
    updateMultipleMaterials(allMaterialIds, true);
    
    try {
      await BatchService.bulkEnableMaterials(batchId, allMaterialIds);
      await loadMaterials(); // Reload to ensure consistency
      toast.success(`Enabled ${allMaterialIds.length} materials`);
    } catch (error) {
      console.error("Enable all error:", error);
      await loadMaterials(); // Reload to revert
      toast.error("Failed to enable materials");
    } finally {
      setUpdating(false);
    }
  };

  const disableAll = async () => {
    const allMaterialIds = getAllMaterialIds(materials);
    if (allMaterialIds.length === 0) {
      toast.info("No materials to disable");
      return;
    }
    
    setUpdating(true);
    // Optimistic update - disable all immediately
    updateMultipleMaterials(allMaterialIds, false);
    
    try {
      // Disable each material individually
      for (const id of allMaterialIds) {
        await BatchService.toggleCourseMaterial(batchId, id);
      }
      await loadMaterials(); // Reload to ensure consistency
      toast.success(`Disabled ${allMaterialIds.length} materials`);
    } catch (error) {
      console.error("Disable all error:", error);
      await loadMaterials(); // Reload to revert
      toast.error("Failed to disable materials");
    } finally {
      setUpdating(false);
    }
  };

  const getAllMaterialIds = (items) => {
    let ids = [];
    items.forEach((item) => {
      ids.push(item.id);
      if (item.children && item.children.length > 0) {
        ids = [...ids, ...getAllMaterialIds(item.children)];
      }
    });
    return ids;
  };

  const getAllEnabledMaterialIds = (items) => {
    let ids = [];
    items.forEach((item) => {
      if (item.is_enabled === true) {
        ids.push(item.id);
      }
      if (item.children && item.children.length > 0) {
        ids = [...ids, ...getAllEnabledMaterialIds(item.children)];
      }
    });
    return ids;
  };

  const getIcon = (type, isExpanded = false) => {
    if (type === "folder") {
      return isExpanded ? (
        <FaFolderOpen className="text-warning me-2" size={18} />
      ) : (
        <FaFolder className="text-warning me-2" size={18} />
      );
    }
    switch (type) {
      case "video":
        return <FaVideo className="text-primary me-2" size={18} />;
      case "pdf":
        return <FaFilePdf className="text-danger me-2" size={18} />;
      default:
        return <FaFileAlt className="text-secondary me-2" size={18} />;
    }
  };

  const filterMaterials = (items) => {
    if (!searchTerm) return items;
    return items.filter((item) => {
      const matchesName = item.name.toLowerCase().includes(searchTerm.toLowerCase());
      if (item.children && item.children.length > 0) {
        const childMatches = filterMaterials(item.children);
        if (childMatches.length > 0) return true;
      }
      return matchesName;
    });
  };

  const renderMaterialTree = (items, level = 0) => {
    const filteredItems = filterMaterials(items);
    
    return filteredItems.map((item) => {
      const isChecked = item.is_enabled === true;
      const isUpdatingThis = updatingMaterialId === item.id;
      
      return (
        <div key={item.id} style={{ marginLeft: `${level * 20}px` }}>
          <div
            className="d-flex align-items-center justify-content-between p-2 border-bottom"
            style={{ backgroundColor: level % 2 === 0 ? "#f8f9fa" : "white" }}
          >
            <div className="d-flex align-items-center">
              {item.type === "folder" && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 me-1 text-decoration-none"
                  onClick={() => toggleFolder(item.id)}
                  disabled={updating}
                >
                  {expandedFolders[item.id] ? "▼" : "▶"}
                </Button>
              )}
              <div
                className="d-flex align-items-center"
                style={{ cursor: item.type === "folder" ? "pointer" : "default" }}
                onClick={() => item.type === "folder" && toggleFolder(item.id)}
              >
                {getIcon(item.type, expandedFolders[item.id])}
                <span className="ms-1">
                  {item.name}
                  {item.type !== "folder" && item.file_size && (
                    <small className="text-muted ms-2">
                      ({(item.file_size / 1024).toFixed(2)} KB)
                    </small>
                  )}
                  {item.type === "folder" && item.children && item.children.length > 0 && (
                    <Badge bg="secondary" className="ms-2" pill>
                      {item.children.length} items
                    </Badge>
                  )}
                </span>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              <Badge bg={isChecked ? "success" : "secondary"} className="px-2 py-1">
                {isChecked ? (
                  <>
                    <FaCheckCircle size={10} className="me-1" /> Visible
                  </>
                ) : (
                  <>
                    <FaTimesCircle size={10} className="me-1" /> Hidden
                  </>
                )}
              </Badge>
              
              <Badge 
                bg={isChecked ? "success" : "danger"} 
                className="px-2 py-1"
                style={{ fontSize: "0.9rem", minWidth: "35px", textAlign: "center" }}
              >
                {isChecked ? "1" : "0"}
              </Badge>
              
              <Form.Check
                type="checkbox"
                id={`checkbox-${item.id}`}
                checked={isChecked}
                onChange={() => handleCheckboxChange(item, isChecked)}
                disabled={updating || isUpdatingThis}
                label=""
                className="me-2"
                style={{ transform: "scale(1.2)" }}
              />
              
              {isUpdatingThis && (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              )}
            </div>
          </div>
          {item.type === "folder" &&
            expandedFolders[item.id] &&
            item.children &&
            item.children.length > 0 &&
            renderMaterialTree(item.children, level + 1)}
        </div>
      );
    });
  };

  const enabledCount = getAllEnabledMaterialIds(materials).length;
  const totalCount = getAllMaterialIds(materials).length;

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading course materials...</p>
      </div>
    );
  }

  if (materials.length === 0) {
    return (
      <Alert variant="info" className="text-center">
        <FaFolder size={40} className="mb-3" />
        <h5>No Course Materials Found</h5>
        <p>
          This course doesn't have any materials yet. Add materials from the
          Course Management section first.
        </p>
      </Alert>
    );
  }

  return (
    <div>
      <Card className="mb-3 bg-light">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={6}>
              <h5 className="mb-0">
                Course: <span className="text-primary">{courseName}</span>
              </h5>
              <small className="text-muted">
                Control which course materials are visible to students in this batch
              </small>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <Badge bg="success" className="p-2">
                  <FaCheckCircle className="me-1" />
                  Enabled: {enabledCount}/{totalCount}
                </Badge>
              </div>
            </Col>
            <Col md={3}>
              <div className="d-flex gap-2">
                <Button
                  variant="success"
                  size="sm"
                  onClick={enableAll}
                  disabled={updating}
                >
                  <FaCheckDouble className="me-1" /> Enable All
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={disableAll}
                  disabled={updating}
                >
                  <FaTimes className="me-1" /> Disable All
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="mb-3">
        <div className="input-group">
          <span className="input-group-text bg-white">
            <FaSearch />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search materials..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <Card>
        <Card.Header className="bg-primary text-white">
          <strong>Course Materials Control Panel</strong>
          <small className="d-block text-white-50">
            Check the box to show materials to students | Folders control ALL items inside them automatically
          </small>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="p-3 bg-light border-bottom">
            <div className="row">
              <div className="col-md-7">
                <strong>Material / Folder</strong>
              </div>
              <div className="col-md-5">
                <strong className="ms-4">Status & Control</strong>
              </div>
            </div>
          </div>
          <div style={{ maxHeight: "500px", overflowY: "auto" }}>
            {renderMaterialTree(materials)}
          </div>
        </Card.Body>
        <Card.Footer className="bg-light">
          <small className="text-muted">
            <FaCheckCircle className="text-success me-1" /> Green = Students can see this material
            <br />
            <FaTimesCircle className="text-secondary me-1" /> Gray = Students cannot see this material
            <br />
            <strong className="text-primary">📁 Folder Behavior:</strong> When you enable/disable a folder, ALL items inside it will be automatically enabled/disabled as well
            <br />
            <strong>💡 Tip:</strong> Use folders to control visibility for multiple items at once
          </small>
        </Card.Footer>
      </Card>
    </div>
  );
};

export default BatchCourseMaterialsTab;